// src/App.js
import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import Layout from "./Website/Layout";
import JobsLayout from "./webideal-jobs/Layout";
import RecruitLayout from "./webideal-recruit/Layout";
import ScrollToTop from "./Website/Components/ScrollToTop";

// Website Pages
import Home from "./Website/Pages/Home";
import About from "./Website/Pages/About";
import Contact from "./Website/Pages/Contact";
import Products from "./Website/Pages/Products";
import ProductJobs from "./Website/Pages/ProductJobs";
import ProductRecruit from "./Website/Pages/ProductRecruit";
import ProductATS from "./Website/Pages/ProductATS";
import ProductHRMS from "./Website/Pages/ProductHRMS";
import Pricing from "./Website/Pages/Pricing";
import Blog from "./Website/Pages/Blog";
import Careers from "./Website/Pages/Careers";
import Resources from "./Website/Pages/Resources";
import Integrations from "./Website/Pages/Integrations";
import AIAutomation from "./Website/Pages/AIAutomation";
import Testimonials from "./Website/Pages/Testimonials";
import WhyWebIdeal from "./Website/Pages/WhyWebIdeal";
import FAQ from "./Website/Pages/FAQ";
import Privacy from "./Website/Pages/Privacy";
import Terms from "./Website/Pages/Terms";
import Admin from "./Website/Pages/Admin";

import HRMSApp from "./HRMS Site/HRMSApp";
import HRMSSetPassword from "./HRMS Site/SetPassword";
import ATSApp from "./ATS/ATSApp";

// Webideal Jobs Pages
import JobsHome from "./webideal-jobs/pages/Home";
import JobsPage from "./webideal-jobs/pages/Jobs";
import CompaniesPage from "./webideal-jobs/pages/Companies";
import Register from "./webideal-jobs/pages/Register";
import Dashboard from "./webideal-jobs/pages/Dashboard";
import Services from "./webideal-jobs/pages/Services";
import Profile from "./webideal-jobs/pages/Profile";
import Auth from "./webideal-jobs/pages/Auth";
import JobDetails from "./webideal-jobs/pages/JobDetails";
import Messages from "./webideal-jobs/pages/Messages";
import ResetPassword from "./webideal-jobs/pages/ResetPassword";
import BlogsPage from "./webideal-jobs/pages/Blogs";
import Notifications from "./webideal-jobs/pages/Notifications";
import Apply from "./webideal-jobs/pages/Apply";

import RecruitDashboard from "./webideal-recruit/pages/Dashboard";
import RecruitJobs from "./webideal-recruit/pages/Jobs";
import CreateJob from "./webideal-recruit/pages/CreateJob";
import JobDetail from "./webideal-recruit/pages/JobDetail";
import Candidates from "./webideal-recruit/pages/Candidates";
import CandidateDetail from "./webideal-recruit/pages/CandidateDetail";
import Pipeline from "./webideal-recruit/pages/Pipeline";
import Interviews from "./webideal-recruit/pages/Interviews";
import Settings from "./webideal-recruit/pages/Settings";
import RecruitAuth from "./webideal-recruit/pages/Auth";
import JobSettings from "./webideal-jobs/pages/Settings";

import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import "./App.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
        <ScrollToTop />
          <Routes>

            {/* --------------------------- */}
            {/* Main Website Routes (Public) */}
            {/* --------------------------- */}
            <Route path="/" element={<Layout currentPageName="Home"><Home /></Layout>} />
            <Route path="/about" element={<Layout currentPageName="About"><About /></Layout>} />
            <Route path="/contact" element={<Layout currentPageName="Contact"><Contact /></Layout>} />
            <Route path="/products" element={<Layout currentPageName="Products"><Products /></Layout>} />
            <Route path="/products/jobs" element={<Layout currentPageName="ProductJobs"><ProductJobs /></Layout>} />
            <Route path="/products/recruit" element={<Layout currentPageName="ProductRecruit"><ProductRecruit /></Layout>} />
            <Route path="/products/ats" element={<Layout currentPageName="ProductATS"><ProductATS /></Layout>} />
            <Route path="/products/hrms" element={<Layout currentPageName="ProductHRMS"><ProductHRMS /></Layout>} />
            <Route path="/pricing" element={<Layout currentPageName="Pricing"><Pricing /></Layout>} />
            <Route path="/blog" element={<Layout currentPageName="Blog"><Blog /></Layout>} />
            <Route path="/careers" element={<Layout currentPageName="Careers"><Careers /></Layout>} />
            <Route path="/resources" element={<Layout currentPageName="Resources"><Resources /></Layout>} />
            <Route path="/integrations" element={<Layout currentPageName="Integrations"><Integrations /></Layout>} />
            <Route path="/ai-automation" element={<Layout currentPageName="AIAutomation"><AIAutomation /></Layout>} />
            <Route path="/testimonials" element={<Layout currentPageName="Testimonials"><Testimonials /></Layout>} />
            <Route path="/why-webideal" element={<Layout currentPageName="WhyWebIdeal"><WhyWebIdeal /></Layout>} />
            <Route path="/faq" element={<Layout currentPageName="FAQ"><FAQ /></Layout>} />
            <Route path="/privacy" element={<Layout currentPageName="Privacy"><Privacy /></Layout>} />
            <Route path="/terms" element={<Layout currentPageName="Terms"><Terms /></Layout>} />
            <Route path="/admin" element={<Layout currentPageName="Admin"><Admin /></Layout>} />

            {/* Protected Core Apps */}
            {/*
              HRMS handles its own login flow internally (LandingPage + isLoggedIn
              state inside HRMSApp). Wrapping it in ProtectedRoute would redirect
              unauthenticated users to /jobs/auth before HRMSApp can render, which
              showed the Job Portal login when users picked "HR Admin".
              
              ATS also handles its own login flow - the /ats/login route is public,
              but individual pages inside ATS are protected by Layout.
            */}
            <Route path="/hrms/*" element={<HRMSApp />} />
            {/* HRMS invite onboarding, target of the approval email link */}
            <Route path="/set-password" element={<HRMSSetPassword />} />
            <Route path="/ats/*" element={<ATSApp />} />

            {/* --------------------------- */}
            {/* Webideal Jobs Module */}
            {/* --------------------------- */}

            <Route
              path="/jobs"
              element={<JobsLayout currentPageName="JobsHome"><JobsHome /></JobsLayout>}
            />

            <Route
              path="/jobs/list"
              element={<JobsLayout currentPageName="Jobs"><JobsPage /></JobsLayout>}
            />

            <Route
              path="/jobs/companies"
              element={<JobsLayout currentPageName="Companies"><CompaniesPage /></JobsLayout>}
            />

            <Route
              path="/jobs/blogs"
              element={<JobsLayout currentPageName="Blogs"><BlogsPage /></JobsLayout>}
            />

            <Route
              path="/jobs/services"
              element={<JobsLayout currentPageName="Services"><Services /></JobsLayout>}
            />

            <Route
              path="/jobs/register"
              element={<JobsLayout currentPageName="Register"><Register /></JobsLayout>}
            />

            {/* Protected Jobs Routes */}
            <Route
              path="/jobs/dashboard"
              element={<ProtectedRoute requireRole="jobseeker"><JobsLayout currentPageName="Dashboard"><Dashboard /></JobsLayout></ProtectedRoute>}
            />

            <Route
              path="/jobs/profile"
              element={<ProtectedRoute><JobsLayout currentPageName="Profile"><Profile /></JobsLayout></ProtectedRoute>}
            />

            <Route
              path="/jobs/messages"
              element={<ProtectedRoute><JobsLayout currentPageName="Messages"><Messages /></JobsLayout></ProtectedRoute>}
            />

            <Route
              path="/jobs/notifications"
              element={<ProtectedRoute><JobsLayout currentPageName="Notifications"><Notifications /></JobsLayout></ProtectedRoute>}
            />

            <Route
              path="/apply/:id"
              element={<ProtectedRoute><JobsLayout currentPageName="Apply"><Apply /></JobsLayout></ProtectedRoute>}
            />

            {/* Public Auth */}
            <Route
              path="/jobs/auth"
              element={<Auth />}
            />

            <Route
              path="/reset-password/:token"
              element={<ResetPassword />}
            />

            <Route
              path="/settings"
              element={<ProtectedRoute><JobsLayout currentPageName="Settings"><JobSettings /></JobsLayout></ProtectedRoute>}
            />

            <Route
              path="/jobs/:id"
              element={<JobsLayout currentPageName="Job Details"><JobDetails /></JobsLayout>}
            />

            {/* --------------------------- */}
            {/* Recruit Module (Employer Protected) */}
            {/* --------------------------- */}
            <Route path="/recruit/dashboard" element={<ProtectedRoute requireRole="employer"><RecruitLayout currentPageName="Dashboard"><RecruitDashboard /></RecruitLayout></ProtectedRoute>} />
            <Route path="/recruit/jobs" element={<ProtectedRoute requireRole="employer"><RecruitLayout currentPageName="Jobs"><RecruitJobs /></RecruitLayout></ProtectedRoute>} />
            <Route path="/recruit/create-job" element={<ProtectedRoute requireRole="employer"><RecruitLayout currentPageName="CreateJob"><CreateJob /></RecruitLayout></ProtectedRoute>} />
            <Route path="/recruit/job-detail" element={<ProtectedRoute requireRole="employer"><RecruitLayout currentPageName="JobDetail"><JobDetail /></RecruitLayout></ProtectedRoute>} />
            <Route path="/recruit/candidates" element={<ProtectedRoute requireRole="employer"><RecruitLayout currentPageName="Candidates"><Candidates /></RecruitLayout></ProtectedRoute>} />
            <Route path="/recruit/candidate-detail" element={<ProtectedRoute requireRole="employer"><RecruitLayout currentPageName="CandidateDetail"><CandidateDetail /></RecruitLayout></ProtectedRoute>} />
            <Route path="/recruit/pipeline" element={<ProtectedRoute requireRole="employer"><RecruitLayout currentPageName="Pipeline"><Pipeline /></RecruitLayout></ProtectedRoute>} />
            <Route path="/recruit/interviews" element={<ProtectedRoute requireRole="employer"><RecruitLayout currentPageName="Interviews"><Interviews /></RecruitLayout></ProtectedRoute>} />
            <Route path="/recruit/settings" element={<ProtectedRoute requireRole="employer"><RecruitLayout currentPageName="Settings"><Settings /></RecruitLayout></ProtectedRoute>} />
            
            <Route path="/recruit/auth" element={<RecruitAuth />} />

            {/* 404 */}
            <Route
              path="*"
              element={
                <Layout currentPageName="NotFound">
                  <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <h1 className="text-4xl">404 | Not Found</h1>
                  </div>
                </Layout>
              }
            />

          </Routes>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
